import { type EntityState } from '@ngrx/entity';

import {
  type CanvasElement,
  type CanvasElementId,
  type CanvasElementType,
  type ElementTransformFunction,
  type PartialPropsOf,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementDataProperties,
  type CanvasElementPropertiesWithData,
} from '@blaze-shared/elements-domain';
import { deepmergeExceptArrays } from '@blaze-shared/shared-util';

import { type CanvasElementDefinitionInternal } from '../../canvas-elements/element-definition';
import { arePropsWithData } from '../../canvas-elements/element-model/element-props-guards';
import { getFormInputNameByType } from '../../canvas-elements/element-model/io';

/**
 * Transforms the element to a new type;
 * Converts the props and slots
 *
 * Order of props (further ones overwrite the previous ones):
 * 1. Element definition
 * 2. Init props
 * 3. Transform element fn
 * 4. Update props
 *
 * @param entityState the state with canvas elements
 * @param elementId the element id to be transformed
 * @param newElementDefinition the definition of the new element
 * @param transformElementFn mapping function transforming the props
 * @param initProps the props which will be applied instead of definition's initial props (to be compliant with {@link generateNewElement} logic), but before transforming the props
 * @param updateProps the props which will be applied after props transformation
 */
export function transformElementTypeMutably<TargetType extends CanvasElementType>(
  entityState: EntityState<CanvasElement>,
  elementId: CanvasElementId,
  newElementDefinition: CanvasElementDefinitionInternal<TargetType>,
  transformElementFn: ElementTransformFunction<CanvasElementType, TargetType>,
  initProps?: PartialPropsOf<TargetType>,
  updateProps?: PartialPropsOf<TargetType>,
): void {
  const element = entityState.entities[elementId];
  if (!element) {
    throw new Error('Element could not be found');
  }

  // Store previous
  const newInitProps = {
    ...(initProps ?? newElementDefinition.initial),
  } as PartialPropsOf<TargetType>;

  // Prepare transformed element
  const newElement = transformElementFn(element);
  if ('props' in newElement) {
    purgeUndefined(newElement.props);
  }

  // Build new base element
  const elementOriginalType = element.type;
  element.type = newElementDefinition.type;

  // If no props previously - do not transform props
  if (!('props' in element) || !element.props) {
    return;
  }

  const elementData = arePropsWithData(element.props) ? element.props.data : undefined;

  // Apply new initial properties
  if (newInitProps) {
    if (elementData && !arePropsWithData(newInitProps)) {
      // NOTE form elements get their updated bindings via the `newInitProps` var
      // elements outside of forms will get their bindings fixed here (if needed)
      (newInitProps as CanvasElementPropertiesWithData).data = updateElementInputBinding(
        elementData,
        elementOriginalType,
        element.type,
      );
    }
    (element as any).props = newInitProps;
  }

  if ('props' in newElement) {
    (element as any).props = deepmergeExceptArrays(element.props as any, newElement.props as any);
  }

  // Apply update props
  if (updateProps) {
    (element as any).props = deepmergeExceptArrays(element.props as any, updateProps as any);
  }

  // TODO: Update slots and children
}

/**
 * Clears the provided object from undefined props.
 * Done mutably and recursively.
 * If all children of certain field are undefined - the parent is set as undefined.
 *
 * Example:
 * ```
 * { a: { b: undefined }, c: { d: { e: undefined, f: 'Abc' } } }
 * ```
 * would be converted to
 * ```
 * { c: { d: { f: 'Abc' } } }
 * ```
 */
function purgeUndefined(obj: any): void {
  for (const key in obj) {
    if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      purgeUndefined(obj[key]);
      if (obj[key] !== null && Object.values(obj[key]).length === 0) {
        obj[key] = undefined;
      }
    }
    if (obj[key] === undefined) {
      delete obj[key];
    }
  }
}

function updateElementInputBinding(
  data: CanvasElementDataProperties[],
  source: CanvasElementType,
  target: CanvasElementType,
): CanvasElementDataProperties[] {
  const sourceBindingKey = getFormInputNameByType(source);
  const targetBindingKey = getFormInputNameByType(target);
  if (sourceBindingKey === targetBindingKey) {
    return data;
  }

  return data?.map((binding) =>
    binding.key !== sourceBindingKey
      ? binding
      : {
          ...binding,
          key: targetBindingKey,
        },
  );
}
